import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { RenderRouteDefault } from "../map-elements/route-default";
import { RenderRouteDirectionsService } from "../map-elements/route-directionsservice";
import { RenderRouteSnapToRoad } from "../map-elements/route-snaptoroad";
import {
  sanitizeTripPointsForRouting,
  snappedPathPlausibility,
} from "../map-elements/routeQuality";
import {
  approxMetersBetween,
  LatLng,
  METERS_PER_DEGREE_LAT,
} from "./geo";

export type RouteRenderMode =
  | "DEFAULT"
  | "SNAP_TO_ROAD"
  | "DIRECTIONS_SERVICE"
  | "POINT_TO_POINT";

// a drawn point carrying the moment the vehicle was there. road-aligned
// paths have far more points than the GPS trip did, so the timestamps are
// derived rather than reported - see attachTimestamps below.
export type TimedPathPoint = { lat: number; lng: number; timestamp: number };

// Which view is active lives in routeViewModes, as one precedence order
// shared by geometry and styling - it used to be decided separately here
// and there, in opposite directions.

const rawPathOf = (points: VehicleMapDataPoint[]): TimedPathPoint[] =>
  points.map((point) => ({
    lat: point.latitude,
    lng: point.longitude,
    timestamp: point.timestamp,
  }));

// running distance along a path, in metres, one entry per point
const cumulativeDistances = (path: { lat: number; lng: number }[]) => {
  const distances = [0];
  for (let i = 1; i < path.length; i++)
    distances.push(distances[i - 1] + approxMetersBetween(path[i - 1], path[i]));
  return distances;
};

// Road-aligned paths come back without timestamps, and the animation runs on
// a wall clock, so every drawn point needs one. Matching by distance along
// the route rather than by index keeps the vehicle's real pace: it still
// crawls where it crawled and covers ground where it was moving.
const attachTimestamps = (
  path: { lat: number; lng: number }[],
  originalPoints: VehicleMapDataPoint[]
): TimedPathPoint[] => {
  if (path.length === 0) return [];
  if (originalPoints.length < 2)
    return path.map((point) => ({
      ...point,
      timestamp: originalPoints[0]?.timestamp ?? 0,
    }));

  const originalPath = originalPoints.map((point) => ({
    lat: point.latitude,
    lng: point.longitude,
  }));
  const originalDistances = cumulativeDistances(originalPath);
  const originalTotal = originalDistances[originalDistances.length - 1];

  const pathDistances = cumulativeDistances(path);
  const pathTotal = pathDistances[pathDistances.length - 1];

  // a route that never moved can't be placed by distance - spread it evenly
  if (originalTotal === 0 || pathTotal === 0) {
    const start = originalPoints[0].timestamp;
    const end = originalPoints[originalPoints.length - 1].timestamp;
    return path.map((point, index) => ({
      ...point,
      timestamp:
        start + ((end - start) * index) / Math.max(1, path.length - 1),
    }));
  }

  // both walks only move forward, so the original index carries across steps
  let originalIndex = 0;
  return path.map((point, index) => {
    const targetDistance = (pathDistances[index] / pathTotal) * originalTotal;
    while (
      originalIndex < originalDistances.length - 2 &&
      originalDistances[originalIndex + 1] < targetDistance
    )
      originalIndex += 1;

    const segmentStart = originalDistances[originalIndex];
    const segmentEnd = originalDistances[originalIndex + 1];
    const segmentLength = segmentEnd - segmentStart;
    const withinSegment =
      segmentLength <= 0
        ? 0
        : Math.max(0, Math.min(1, (targetDistance - segmentStart) / segmentLength));

    const startTime = originalPoints[originalIndex].timestamp;
    const endTime = originalPoints[originalIndex + 1].timestamp;
    return {
      ...point,
      timestamp: startTime + (endTime - startTime) * withinSegment,
    };
  });
};

// Snap-to-road and directions are billed Google calls, and a fleet-sized
// selection multiplies them by every trip on screen. Results are keyed on
// the trip's actual shape, so redraws, replays and route-view switches all
// reuse what was already fetched.
const resolvedPathCache = new Map<string, Promise<TimedPathPoint[]>>();

// A trip that is still being driven grows on every refresh, and its key
// carries its shape - so each refresh leaves the previous version behind.
// Oldest out first (Map keeps insertion order) to stop a long session
// accumulating every intermediate version of every trip.
const MAX_CACHED_PATHS = 400;

// The same results once settled, so a caller can have them without a round
// trip. Awaiting the promise above costs a microtask even on a cache hit,
// and in that window a route switched to another view wears the new view's
// styling on the old view's geometry.
const settledPathCache = new Map<string, TimedPathPoint[]>();

const rememberPath = (key: string, pending: Promise<TimedPathPoint[]>) => {
  resolvedPathCache.set(key, pending);
  pending
    .then((path) => {
      // only while this key is still the live one - a trip that grew has
      // already moved on to a new key, and its old path must not come back
      if (resolvedPathCache.get(key) === pending)
        settledPathCache.set(key, path);
    })
    .catch(() => {});
  while (resolvedPathCache.size > MAX_CACHED_PATHS) {
    const oldest = resolvedPathCache.keys().next();
    if (oldest.done) break;
    resolvedPathCache.delete(oldest.value);
    settledPathCache.delete(oldest.value);
  }
};

// What `resolveRoutePath` would return, if it is already known. Undefined
// means "not resolved yet", never "no route" - the caller keeps what it has
// and waits for the async pass rather than drawing something wrong.
export const peekResolvedPath = (
  mode: RouteRenderMode,
  tripKey: string,
  points: VehicleMapDataPoint[]
): TimedPathPoint[] | undefined => {
  if (points.length < 2) return undefined;
  // the raw fixes - nothing to fetch, so it is always available
  if (mode === "POINT_TO_POINT") return rawPathOf(points);
  return settledPathCache.get(cacheKeyFor(mode, tripKey, points));
};

// A 32-bit FNV-1a over the trip's coordinates.
// Coordinates are rounded to ~0.1m first, so a genuine correction changes
// the hash while float noise does not.
const COORD_HASH_PRECISION = 1e6;

const shapeHashOf = (points: VehicleMapDataPoint[]) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < points.length; i++) {
    hash ^= Math.round(points[i].latitude * COORD_HASH_PRECISION);
    hash = Math.imul(hash, 0x01000193);
    hash ^= Math.round(points[i].longitude * COORD_HASH_PRECISION);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

const cacheKeyFor = (
  mode: RouteRenderMode,
  tripKey: string,
  points: VehicleMapDataPoint[]
) => {
  const first = points[0];
  const last = points[points.length - 1];
  return `${mode}|${tripKey}|${points.length}|${first.timestamp}|${last.timestamp}|${shapeHashOf(points)}`;
};

// Perpendicular distance from `point` to the SEGMENT a->b, in metres.
//
// Clamped to the SEGMENT. It was previously derived from the triangle's
// area (Heron), which measures distance to the INFINITE LINE through a and
// b - and that is only the same thing while the path stays between them.
//
// An out-and-back breaks it: a street driven up and back puts every point
// of the excursion on the line through its two ends, so the old version
// scored the turnaround 0m away no matter how far out it actually went.
// The simplifier then dropped the whole excursion and drew a chord across
// it - the phantom diagonal, on routes that never went that way. A sweeper
// or collection round is almost entirely out-and-backs, so it showed there
// first and worst.
export const metresFromSegment = (
  point: LatLng,
  a: LatLng,
  b: LatLng
): number => {
  const lngScale =
    METERS_PER_DEGREE_LAT *
    Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);

  const pointX = (point.lng - a.lng) * lngScale;
  const pointY = (point.lat - a.lat) * METERS_PER_DEGREE_LAT;
  const segmentX = (b.lng - a.lng) * lngScale;
  const segmentY = (b.lat - a.lat) * METERS_PER_DEGREE_LAT;

  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;
  if (segmentLengthSquared === 0)
    return Math.sqrt(pointX * pointX + pointY * pointY);

  const projection =
    (pointX * segmentX + pointY * segmentY) / segmentLengthSquared;
  const clamped = Math.max(0, Math.min(1, projection));

  const offsetX = pointX - clamped * segmentX;
  const offsetY = pointY - clamped * segmentY;
  return Math.sqrt(offsetX * offsetX + offsetY * offsetY);
};

// Ramer-Douglas-Peucker. Uniform decimation would thin a roundabout as hard
// as a straightaway; this keeps points where the path actually bends and
// drops them where it doesn't, which is what a road-matcher needs.
export const simplifyPath = <T extends { latitude: number; longitude: number }>(
  points: T[],
  toleranceMetres: number
): T[] => {
  if (points.length < 3) return points;

  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const asLatLng = (p: T): LatLng => ({ lat: p.latitude, lng: p.longitude });

  // explicit stack rather than recursion - a chatty trip is deep enough to
  // blow the call stack
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [start, end] = stack.pop() as [number, number];
    if (end <= start + 1) continue;

    let worstIndex = -1;
    let worstDistance = toleranceMetres;
    for (let i = start + 1; i < end; i++) {
      const distance = metresFromSegment(
        asLatLng(points[i]),
        asLatLng(points[start]),
        asLatLng(points[end])
      );
      if (distance > worstDistance) {
        worstDistance = distance;
        worstIndex = i;
      }
    }
    if (worstIndex === -1) continue;
    keep[worstIndex] = 1;
    stack.push([start, worstIndex], [worstIndex, end]);
  }

  return points.filter((_, index) => keep[index] === 1);
};

const computePath = async (
  mode: RouteRenderMode,
  tripIndex: number,
  rawPoints: VehicleMapDataPoint[]
): Promise<TimedPathPoint[]> => {
  // Clean invalid (0,0)/out-of-range fixes and teleport glitches before
  // anything routes or times them. Done once here so the routing methods,
  // the plausibility gate, attachTimestamps (distance-matched - a spike would
  // distort the timing), and the raw-GPS fallback all see the same clean set.
  const allPoints = sanitizeTripPointsForRouting(rawPoints);
  if (allPoints.length < 2) return rawPathOf(allPoints);

  // NOT thinned here. The DEFAULT hybrid classifies each segment by how far
  // apart its fixes are, and forces anything with a gap over
  // SAFE_SNAP_LIMIT_METERS to SPARSE - which routes it to Directions, and
  // Directions invents a route rather than snapping the fixes it was given.
  //
  // Thinning ahead of that widened every gap by roughly the reduction ratio,
  // so dense trips were reclassified sparse and came back as fabricated
  // routes that crossed streets the asset never drove. The reduction now
  // happens inside gSnapPointsToRoad, below the segmenter, where the 100-
  // point batching it exists to reduce actually lives.
  const points = allPoints;

  // any of these can fail on a bad segment or a quota trip - the raw GPS
  // path is always a usable answer, so never let a route go missing
  try {
    if (mode === "SNAP_TO_ROAD") {
      const snapped = await RenderRouteSnapToRoad.getComputedRoutePoints({
        tripIndex,
        sanitizedTripPoints: points,
        refString: "MULTI",
      });
      // Same quality gate the single-day DEFAULT hybrid uses: a snapped
      // result that jumped onto parallel streets (or grossly inflated) is
      // rejected in favour of the raw-GPS straight line, so the multi-day
      // and replay views can't draw the through-the-houses artefact either.
      const verdict = snappedPathPlausibility(snapped, points);
      if (!verdict.isPlausible) return rawPathOf(allPoints);
      const path = snapped.map((item) => ({
        lat: item.location.latitude,
        lng: item.location.longitude,
      }));
      return path.length >= 2
        ? attachTimestamps(path, allPoints)
        : rawPathOf(allPoints);
    }

    if (mode === "DIRECTIONS_SERVICE") {
      const directions =
        await RenderRouteDirectionsService.getComputedRoutePoints({
          tripIndex,
          sanitizedTripPoints: points,
          refString: "MULTI",
        });
      const path = directions.map((item) => ({
        lat: (item.location as any).latitude,
        lng: (item.location as any).longitude,
      }));
      return path.length >= 2
        ? attachTimestamps(path, allPoints)
        : rawPathOf(allPoints);
    }

    // DEFAULT - the hybrid the single-day view uses: dense stretches snapped
    // to road, sparse ones reconstructed through directions, gaps bridged.
    // preRender does the computing and touches nothing on the map, so the
    // instance needs no real props.
    const renderer = new RenderRouteDefault({
      props: {} as any,
      tripIndex,
      sanitizedTripPoints: points,
      interpolatedTripPoints: [],
      polylineConfig: {
        strokeColor: "#000000",
        strokeOpacity: 0,
        strokeWeight: 0,
      },
    });
    const chunks = await renderer.preRender();
    const path = chunks
      .flatMap((chunk) => chunk.points)
      .map((item) => ({
        lat: item.location.latitude,
        lng: item.location.longitude,
      }));
    return path.length >= 2
      ? attachTimestamps(path, allPoints)
      : rawPathOf(allPoints);
  } catch {
    return rawPathOf(allPoints);
  }
};

// resolves one trip's drawn path for the given route view. POINT_TO_POINT is
// the raw GPS fixes; everything else is road-aligned and cached.
export const resolveRoutePath = (
  mode: RouteRenderMode,
  tripKey: string,
  tripIndex: number,
  points: VehicleMapDataPoint[]
): Promise<TimedPathPoint[]> => {
  if (points.length < 2 || mode === "POINT_TO_POINT")
    return Promise.resolve(rawPathOf(points));

  const key = cacheKeyFor(mode, tripKey, points);
  const cached = resolvedPathCache.get(key);
  if (cached) return cached;

  const pending = computePath(mode, tripIndex, points);
  rememberPath(key, pending);
  return pending;
};
